import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { ErrorResponse, GlobalResponseObject } from '@shahi-analytics/backend-utils';
import axios from 'axios';
import { In, Repository } from 'typeorm';
import configuration from '../../config/configuration';
import { LineEntity } from '../entities/line.entity';
import { OperationEntity } from '../entities/operation.entity';
import { OperatorEntity } from '../entities/operator.entity';
import { ReasonEntity } from '../entities/reason.entity';
import { StyleOperationsEntity } from '../entities/style-operations.entity';
import { StyleEntity } from '../entities/style.entity';
import { LineActualDayTrackEntity } from '../entities/transactions/line-actual-track.entity';
import { LineDownTimeEntity } from '../entities/transactions/line-down-time.entity';
import { LineEfficiencyDayTrackEntity } from '../entities/transactions/line-efficiency.entity';
import { LineDayPlanEntity } from '../entities/transactions/line-plan';
import { MigrationLogEntity } from '../entities/transactions/migration-log.entity';
import { OperatorEfficiencyDayTrackEntity } from '../entities/transactions/operator-efficiency-track.entity';
import { WorkstationEntity } from '../entities/workstation.entity';
import { DbNamesEnum } from './db-connection/db-names.enum';
import { ShahiDbConnectionsService } from './db-connection/shahi-db-connection';
import { AppTableNamesEnum } from './models/app-table-names.enum';
import { MigrationStatusEnum } from './models/migration-status.enum';
import { ReasonPriorityEnum } from './models/reason-priority-enum';
import { UnitDateHourRequest } from './models/unit-date-hour.request';
import { MigrationService } from './migration.service';
import { UnitEntity } from '../entities/unit.entity';
import { UnitDayHourEntity } from '../entities/unit-day-hours.entity';
import { UnitBreakEntity } from '../entities/unit-break.entity';
import { UnitDayBreakEntity } from '../entities/unit-day-break.entity';
import { MigrationHelperService } from './migration-helper.service';

@Injectable()
export class MigrationTriggerService {

    constructor(
        @InjectRepository(LineEntity) private lineEnt: Repository<LineEntity>,
        @InjectRepository(MigrationLogEntity) private migLogEnt: Repository<MigrationLogEntity>,
        @InjectRepository(UnitEntity) private unitEnt: Repository<UnitEntity>,
        
        private shahiDb: ShahiDbConnectionsService,
        private migrationService: MigrationService,
        private migHelperService: MigrationHelperService
    ) {
        
    }

    // Trugegrs the per unit migration for all the active units
    async triggerHourMigrationForAllUnits(hour: number, currentDate: string): Promise<GlobalResponseObject> {   
        const units = await this.unitEnt.find({select: ['unitId', 'id', 'unitName'], where: {isActive: true} });
        if(units.length == 0) {
            throw new ErrorResponse(0, 'No active units found to run the migration');
        }
        let missingWorkingHourUnits = ''; 
        // for each and every unit trigegr the line level data migration
        for(const unit of units) {
            /**
             * 1. the working hours must be defined for all units
             * 2. the break times if any must be defined for all units
             */
            const workingHours = await this.migHelperService.getFirstWorkingHourInUnitForDate(unit.unitId, currentDate);
            if(!workingHours) {
                missingWorkingHourUnits += ' UNIT - '+unit.unitId;
            }
            await this.triggerHourMigrationForSpecificUnit(unit.unitId, hour, currentDate);
        }
        console.log('All Units migration started');
        return new GlobalResponseObject(true, 0, 'Unit migration triggered. Missing : '+ missingWorkingHourUnits);
    }

    // Each unit migration for the given hour
    // A predecessor to just log the migraiton log an then trigger the lines level migration
    async triggerHourMigrationForSpecificUnit(unit: string, hour: number, currentDate: string): Promise<GlobalResponseObject> {
        // get the unit level shift hours
        const unitHours = await this.migHelperService.getUnitActualWorkingHoursForDay(unit, currentDate);
        if(!unitHours.includes(hour)) {
            return new GlobalResponseObject(true, 0, 'Unit is not running in this hour');
        }
        const tables = Object.values(AppTableNamesEnum);
        const logEnts: MigrationLogEntity[] = [];
        const lines = await this.lineEnt.find({ select:['lineId'], where: { unitId: unit, isActive: true }});
        for(const line of lines) {
            for(const tableName of tables) {
                // check if the records already exist
                const rec = await this.migLogEnt.findOne({ select: ['id'], where: {unit: unit, lineId: line.lineId, hour: hour, migrationDate: currentDate, tableName: tableName} });
                if(!rec) {
                    const migLogEnt = new MigrationLogEntity();
                    migLogEnt.hour = hour;
                    migLogEnt.lineId = line.lineId;
                    migLogEnt.tableName = tableName;
                    migLogEnt.migrationDate = currentDate;
                    migLogEnt.unit = unit;
                    migLogEnt.status = MigrationStatusEnum.STARTED;
                    logEnts.push(migLogEnt);
                }
            }
        }
        if(logEnts.length > 0) {
            await this.migLogEnt.save(logEnts);
        }
        const linesTriggerReq = new UnitDateHourRequest();
        linesTriggerReq.unit = unit;
        linesTriggerReq.hour = hour;
        linesTriggerReq.date = currentDate;
        await this.triggerHourMigrationForAllLinesInUnit(linesTriggerReq);
        console.log('Sepcific Unit migration started');
        return new GlobalResponseObject(true, 0, 'Migration triggered successfully');
    }

    // Triggers the each line level migration ASYNC API call for the given unit and hour
    async triggerHourMigrationForAllLinesInUnit(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        const port =  configuration().port;
        const host = configuration().selfHost;
        const lines = await this.lineEnt.find({ select: ['lineId'], where: { unitId: req.unit, isActive: true } });
        const linesSet = new Set<string>();
        lines.forEach(r => linesSet.add(r.lineId));
        for(const lineId of linesSet) {
            const lineReq = new UnitDateHourRequest();
            lineReq.unit = req.unit;
            lineReq.lineId = lineId;
            lineReq.hour = req.hour;
            lineReq.date = req.date;
            
            axios.post(configuration().appSepcific.masterDataMigrationUrl, lineReq).catch(err => {
                this.migLogEnt.update({ unit: req.unit, hour: req.hour, lineId: lineId }, { failReason: err.message });
            });
            axios.post(configuration().appSepcific.trackingDataMigrationUrl, lineReq).catch(err => {
                this.migLogEnt.update({ unit: req.unit, hour: req.hour, lineId: lineId}, { failReason: err.message });
            });
        }
        return new GlobalResponseObject(true, 0, 'Line level data migration completed');
    }


    // END Point
    async triggerMasterDataHourMigrationForSpecificLine(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        // const lines = await this.migLogEnt.find({ select: ['lineId', 'tableName','status'], where: { unit: req.unit, migrationDate: req.date, status: MigrationStatusEnum.STARTED, hour: req.hour, lineId: req.lineId} });
        const line = await this.lineEnt.findOne({ select: ['lineId'], where: { unitId: req.unit, isActive: true, lineId: req.lineId} });
        if(line) {
            this.migrateMasterDataForSpecificLine(req);
        }
        return new GlobalResponseObject(true, 0, 'Line level master data migration triggered');
    }

    // END Point
    async triggerTrackingDataHourMigrationForSpecificLine(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        // const lines = await this.migLogEnt.find({ select: ['lineId', 'tableName','status'], where: { unit: req.unit, migrationDate: req.date, status: MigrationStatusEnum.STARTED, hour: req.hour, lineId: req.lineId} });
        const line = await this.lineEnt.findOne({ select: ['lineId'], where: { unitId: req.unit, isActive: true, lineId: req.lineId} });
        if(line) {
            this.migrateTrackingDataForSpecificLine(req);
        }
        return new GlobalResponseObject(true, 0, 'Line level tracking data migration triggered');
    }

    async migrateMasterDataForSpecificLine(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        await this.migrationService.createWorkstations(req);
        await this.migrationService.createOperators(req);
        await this.migrationService.createOperations(req);
        await this.migrationService.createStylesAndStyleOperations(req);
        await this.migrationService.createReasons(req);
        return new GlobalResponseObject(true, 0 ,'Master data created for the line : ' + req.lineId);
    }

    async migrateTrackingDataForSpecificLine(req: UnitDateHourRequest): Promise<GlobalResponseObject> {
        await this.migrationService.createLineActualDayTrack(req);
        // await this.migrationService.createOperatorEfficiency(req);
        // await this.migrationService.createLineEfficiency(req);
        await this.migrationService.createLineDowntime(req);
        return new GlobalResponseObject(true, 0 ,'Master data');
    }
    

    async dumpData(): Promise<GlobalResponseObject> {
        return new GlobalResponseObject(true, 0, 'Line level data migration completed');
    }





}

